import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Depreciation = () => {
  // State variables
  const [assetCost, setAssetCost] = useState("");
  const [salvageValue, setSalvageValue] = useState("");
  const [usefulLife, setUsefulLife] = useState("");
  const [depreciationRate, setDepreciationRate] = useState("");
  const [depreciationMethod, setDepreciationMethod] = useState("straight-line");
  const [depreciationValue, setDepreciationValue] = useState(0);

  // Update depreciation value whenever input changes
  useEffect(() => {
    let value = 0;
    if (depreciationMethod === "straight-line") {
      // Straight-line depreciation formula
      value = (assetCost - salvageValue) / usefulLife || 0; // avoid division by zero
    } else if (depreciationMethod === "declining-balance") {
      // Declining balance depreciation formula
      value = assetCost * (depreciationRate / 100) || 0; // handle empty inputs
    }
    setDepreciationValue(value);

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [
    assetCost,
    salvageValue,
    usefulLife,
    depreciationRate,
    depreciationMethod,
  ]); // Recalculate on input change

  // Event handlers
  const handleAssetCostChange = (e) =>
    setAssetCost(parseFloat(e.target.value) || 0);
  const handleSalvageValueChange = (e) =>
    setSalvageValue(parseFloat(e.target.value) || 0);
  const handleUsefulLifeChange = (e) =>
    setUsefulLife(parseFloat(e.target.value) || 0);
  const handleDepreciationRateChange = (e) =>
    setDepreciationRate(parseFloat(e.target.value) || 0);
  const handleDepreciationMethodChange = (e) =>
    setDepreciationMethod(e.target.value);

  return (
    <Layout title="Depreciation Calculator" path="depreciation">
      <section className=" section-padding" id="depreciation-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Depreciation Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Depreciation Calculator</h3>
                <div className="container">
                  {/* Input Fields for Depreciation Calculation */}
                  <div className="row gy-3">
                    {/* Asset Cost */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="assetCost" className="form-label">
                        Asset Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Asset Cost:</strong> The original purchase price of the asset."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="assetCost"
                        placeholder="Enter asset cost"
                        value={assetCost}
                        onChange={handleAssetCostChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the initial cost of the asset
                      </small> */}
                    </div>

                    {/* Salvage Value */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="salvageValue" className="form-label">
                        Salvage Value (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Salvage Value:</strong> The estimated residual value of the asset at the end of its useful life."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="salvageValue"
                        placeholder="Enter salvage value"
                        value={salvageValue}
                        onChange={handleSalvageValueChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the estimated value at the end of its useful life
                      </small> */}
                    </div>

                    {/* Useful Life */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="usefulLife" className="form-label">
                        Useful Life (years)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Useful Life:</strong> The estimated number of years the asset will be in service."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="usefulLife"
                        placeholder="Enter asset's useful life"
                        value={usefulLife}
                        onChange={handleUsefulLifeChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the duration the asset is expected to be used
                      </small> */}
                    </div>

                    {/* Depreciation Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="depreciationRate" className="form-label">
                        Depreciation Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Depreciation Rate:</strong> The percentage rate at which the asset loses value each year."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="depreciationRate"
                        placeholder="Enter depreciation rate"
                        value={depreciationRate}
                        onChange={handleDepreciationRateChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the yearly depreciation percentage
                      </small> */}
                    </div>

                    {/* Depreciation Method */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="depreciationMethod"
                        className="form-label"
                      >
                        Depreciation Method
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Depreciation Method:</strong> The method used to calculate depreciation, such as Straight-Line or Declining Balance."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        className="form-select"
                        id="depreciationMethod"
                        value={depreciationMethod}
                        onChange={handleDepreciationMethodChange}
                      >
                        <option value="straight-line">Straight-Line</option>
                        <option value="declining-balance">
                          Declining Balance
                        </option>
                      </select>
                      {/* <small className="form-text text-muted">
                        Choose the depreciation method
                      </small> */}
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Depreciation Value:{" "}
                        <span className="fw-bold ms-3">
                          {depreciationValue.toFixed(2)}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Depreciation Calculator
                </h3>

                {/* What is a Depreciation Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Depreciation Calculator?
                </h4>
                <p>
                  A **Depreciation Calculator** helps in determining the
                  reduction in value of an asset over time. This tool is
                  essential for businesses to calculate the expense associated
                  with an asset’s depreciation, aiding in financial reporting
                  and planning.
                </p>

                {/* Why Use a Depreciation Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Depreciation Calculator?
                </h4>
                <p>
                  There are several benefits of using a depreciation calculator:
                </p>
                <ul>
                  <li>
                    <strong>Aids in Accurate Asset Valuation:</strong> Ensures
                    that businesses have an accurate representation of asset
                    value over time.
                  </li>
                  <li>
                    <strong>Helps Allocate Expenses:</strong> Distributes the
                    cost of an asset over its useful life, aiding in proper
                    expense management.
                  </li>
                  <li>
                    <strong>Useful for Tax and Financial Reporting:</strong>{" "}
                    Assists in compliance with tax regulations and enhances
                    clarity in financial statements.
                  </li>
                </ul>

                {/* Suitable for */}
                <h4 className="mt-4 mb-3">
                  Who is this Calculator Suitable For?
                </h4>
                <p>This calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing Companies</strong>
                  </li>
                  <li>
                    <strong>Asset-Intensive Industries</strong>
                  </li>
                  <li>
                    <strong>Accounting Firms</strong>
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  What is the purpose of calculating depreciation?
                </h4>
                <p>
                  Calculating depreciation helps businesses spread the cost of
                  an asset over its useful life, ensuring more accurate
                  financial reporting and compliance with accounting standards.
                </p>

                <h4 className="mt-4 mb-3">
                  What are the common methods of depreciation?
                </h4>
                <p>
                  The common methods include straight-line depreciation, where
                  the cost is divided evenly over the asset’s lifespan, and
                  declining balance depreciation, which applies a fixed rate to
                  the book value each period.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Depreciation;
